// Browser context for server-side (sGTM) tracking, modelled on tickethub-plugin's
// gatewayTools. The payload shape (marketing.*, device.*, page_*) matches what the
// "[sGTM] ..." containers read with their Event Data variables, so the ziz container can
// use the same variables as the Tourism Group ones.
//
// The purchase is sent by the backend once MultiSafepay confirms the payment, when the
// patient is no longer on the site; the context below is therefore collected at checkout,
// posted along with the order and replayed from the server.

const STORAGE_KEY = 'sgtm_marketing';

// Stored attribution older than this is dropped (a later purchase is no longer that visit's).
const ATTRIBUTION_TTL_MS = 30 * 24 * 60 * 60 * 1000;

// The payment return page (msp-checkout-status, and the legacy checkout-status; /en on the
// English site) is reached from the payment provider or bank, so its external referrer is
// not a new landing. Matched as the whole path, so other pages are never excluded.
const PAYMENT_RETURN_PATH = /^\/(?:en\/)?(?:msp-)?checkout-status\/?$/;

const QUERY_PARAMS = [
  'gclid', 'gbraid', 'wbraid', 'gclsrc', 'dclid', 'gad_source',
  'fbclid', 'ttclid', 'msclkid',
  'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'utm_id',
];

// Never throws: a malformed cookie (e.g. `_fbc=%`) must not break the checkout it rides on,
// so an undecodable value counts as absent.
const getCookie = (name) => {
  const prefix = `${name}=`;
  const cookie = document.cookie.split('; ').find((c) => c.startsWith(prefix));
  if (!cookie) return null;
  try {
    return decodeURIComponent(cookie.slice(prefix.length));
  } catch {
    return null;
  }
};

const readStored = () => {
  try {
    const stored = JSON.parse(window.localStorage.getItem(STORAGE_KEY)) || {};
    return Date.now() - (stored.captured_at || 0) < ATTRIBUTION_TTL_MS ? stored : {};
  } catch {
    return {};
  }
};

const writeStored = (data) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    // Storage blocked (private mode): only this page view's values go out.
  }
};

/**
 * GA4 client id from the _ga cookie (GA1.1.<random>.<timestamp> -> <random>.<timestamp>).
 * Null when GA has not set it (e.g. no analytics consent) - no synthetic id is invented.
 */
const getClientId = () => {
  const parts = (getCookie('_ga') || '').split('.');
  return parts.length >= 4 ? `${parts[2]}.${parts[3]}` : null;
};

/**
 * Session id/number per GA4 property from the _ga_<ID> cookies, in either the GS1
 * (GS1.1.<sid>.<count>...) or GS2 (GS2.1.s<sid>$o<count>$...) format. All properties are
 * sent: the backend picks the one configured in GA4_MEASUREMENT_ID, so the property id
 * lives in the environment only.
 */
const getGaSessions = () => document.cookie.split('; ')
  .filter((cookie) => cookie.startsWith('_ga_'))
  .reduce((sessions, cookie) => {
    const [name, value = ''] = cookie.split('=');
    const match = value.match(/^GS2\.\d+\.s(\d+)\$o(\d+)/) || value.match(/^GS1\.\d+\.(\d+)\.(\d+)/);
    if (match) {
      // eslint-disable-next-line no-param-reassign
      sessions[`G-${name.slice(4)}`] = { session_id: match[1], session_count: match[2] };
    }
    return sessions;
  }, {});

/**
 * Whether Consent Mode (set by Cookiebot through GTM) currently grants a consent type,
 * e.g. ad_storage or ad_user_data. No state at all - the banner did not load - is not consent.
 */
export const isConsentGranted = (type) => {
  const entry = window.google_tag_data?.ics?.entries?.[type] || {};
  const value = entry.update ?? entry.default;
  return value === true || value === 'granted';
};

/**
 * Consent mode state as a GCS string:
 * "G1" + ad_storage + analytics_storage, e.g. G111 = both granted.
 */
const getGcs = () => {
  const granted = (type) => (isConsentGranted(type) ? '1' : '0');
  return `G1${granted('ad_storage')}${granted('analytics_storage')}`;
};

/**
 * An email normalized the way Google hashes user-provided data: trimmed and lowercased,
 * and for gmail.com / googlemail.com without the dots in the local part.
 */
export const normalizeEmail = (email) => {
  const normalized = String(email ?? '').trim().toLowerCase();
  const at = normalized.lastIndexOf('@');
  if (at < 1) {
    return normalized;
  }

  const domain = normalized.slice(at + 1);
  if (domain !== 'gmail.com' && domain !== 'googlemail.com') {
    return normalized;
  }

  return `${normalized.slice(0, at).replace(/\./g, '')}@${domain}`;
};

/**
 * User-provided data for the purchase (enhanced conversions, ZIZ-1312): the SHA-256 hash of
 * the normalized email, only when Consent Mode grants ad_user_data. The plain email never
 * leaves this function. Null - never a rejection - without consent, without an email, or
 * when the browser cannot hash (no Web Crypto), so the checkout it rides on never fails.
 */
export const getUserData = async (email) => {
  const normalized = normalizeEmail(email);
  if (!normalized || !isConsentGranted('ad_user_data') || !window.crypto?.subtle) {
    return null;
  }

  try {
    const bytes = new TextEncoder().encode(normalized);
    const digest = await window.crypto.subtle.digest('SHA-256', bytes);
    const hash = Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0'))
      .join('');

    return { sha256_email_address: hash };
  } catch {
    return null;
  }
};

/**
 * Remember the landing that brought the patient in: its click ids / UTMs are gone by the
 * time they reach checkout (consult -> results -> checkout), so this runs on every page load.
 *
 * A new landing - campaign parameters in the URL, or arriving from another site - replaces
 * the stored record as a whole, so values of an earlier campaign never mix into it. Internal
 * navigation, direct visits and returns from the payment provider keep the current record,
 * which expires after ATTRIBUTION_TTL_MS and is cleared once a purchase is made.
 */
export const captureMarketingParams = () => {
  const params = new URLSearchParams(window.location.search);
  const campaign = Object.fromEntries(QUERY_PARAMS
    .filter((key) => params.get(key))
    .map((key) => [key, params.get(key)]));

  if (PAYMENT_RETURN_PATH.test(window.location.pathname)) {
    return;
  }

  let externalReferrer = false;
  try {
    externalReferrer = !!document.referrer
      && new URL(document.referrer).hostname !== window.location.hostname;
  } catch {
    // Unparsable referrer: treat as internal.
  }

  const hasStored = !!readStored().captured_at;
  if (Object.keys(campaign).length === 0 && !externalReferrer && hasStored) {
    return;
  }

  writeStored({
    ...campaign,
    landing_page: window.location.href,
    first_referrer: document.referrer || null,
    captured_at: Date.now(),
  });
};

const ENTRY_PATH_KEY = 'ziz_entry_path';

/**
 * Remember the first page of this visit (the browser session), for consult_start's
 * entry_path (ZIZ-1308). Unlike the attribution record, it starts over with every visit.
 */
export const captureEntryPath = () => {
  try {
    if (!window.sessionStorage.getItem(ENTRY_PATH_KEY)) {
      window.sessionStorage.setItem(ENTRY_PATH_KEY, window.location.pathname);
    }
  } catch {
    // Storage blocked: getEntryPath falls back to the current page.
  }
};

/**
 * Path (no query string) of the first page of this visit, or the current path when it
 * is unknown.
 */
export const getEntryPath = () => {
  try {
    return window.sessionStorage.getItem(ENTRY_PATH_KEY) || window.location.pathname;
  } catch {
    return window.location.pathname;
  }
};

/**
 * Forget the landing once it has been credited with a purchase.
 */
export const clearMarketingParams = () => {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Storage blocked: nothing was stored.
  }
};

/**
 * Everything sGTM needs to attribute a server-side event to this browser.
 */
export const getSgtmContext = () => ({
  marketing: {
    ...readStored(),
    client_id: getClientId(),
    ga_sessions: getGaSessions(),
    user_agent: navigator.userAgent,
    fbp: getCookie('_fbp'),
    fbc: getCookie('_fbc'),
    gcs: getGcs(),
  },
  device: {
    language: navigator.language,
    screen_resolution: `${window.screen.width}x${window.screen.height}`,
    viewport_size: `${window.innerWidth}x${window.innerHeight}`,
    colors: `${window.screen.colorDepth}-bit`,
  },
  page_hostname: window.location.hostname,
  page_location: window.location.href,
  page_path: window.location.pathname,
  page_referrer: document.referrer || null,
  page_title: document.title,
});
